import moment from 'moment';

const DATE_STRINGS = Object.freeze({
  lastMonth: 'last month',
  lastWeek: 'last week',
  lastYear: 'last year',
  monthsAgo: 'months ago',
  thisMonth: 'this month',
  thisWeek: 'this week',
  thisYear: 'this year',
  today: 'today',
  weeksAgo: 'weeks ago',
  yearsAgo: 'years ago',
  yesterday: 'yesterday',
});

export const GROUP_BY = Object.freeze({
  daily: 0,
  weekly: 1,
  monthly: 2,
  yearly: 3,
});

function _getGroups(entries, dateFieldName, dateFormat, groupBy, baseDate) {
  const groupedEntries = {};

  entries.forEach((entry) => {
    const dateEntry = moment(entry[dateFieldName], dateFormat);
    const relativeDate = DateGroupingHelper.getRelativeDate(dateEntry, groupBy, baseDate);

    if (!groupedEntries[relativeDate]) {
      groupedEntries[relativeDate] = {
        dateEntry: dateEntry,
        entries: [],
        relativeDate: relativeDate,
      };
    }

    groupedEntries[relativeDate].entries.push(entry);
  });

  // Sorting
  const groups = Object.values(groupedEntries);
  groups.sort((left, right) => {
    return moment(right.dateEntry).format('X')-moment(left.dateEntry).format('X');
  });

  groups.forEach((group) => delete group.dateEntry);
  return groups;
}

function _getWeeksDifference(baseDate, dateEntry) {
  const isoWeeksDifference = baseDate.isoWeek() - dateEntry.isoWeek();
  const yearsDifference = baseDate.isoWeekYear() - dateEntry.isoWeekYear();

  if (yearsDifference === 0) {
    return isoWeeksDifference;
  }

  return ((yearsDifference * 52) + isoWeeksDifference);
}

function _getYearsDifference(baseDate, dateEntry) {
  return baseDate.year() - dateEntry.year();
}

export default class DateGroupingHelper {
  static getGroupsByDay(entries, dateFieldName, dateFormat, baseDate = moment()) {
    return _getGroups(entries, dateFieldName, dateFormat, GROUP_BY.daily, baseDate);
  }

  static getGroupsByWeek(entries, dateFieldName, dateFormat, baseDate = moment()) {
    return _getGroups(entries, dateFieldName, dateFormat, GROUP_BY.weekly, baseDate);
  }

  static getGroupsByYear(entries, dateFieldName, dateFormat, baseDate = moment()) {
    return _getGroups(entries, dateFieldName, dateFormat, GROUP_BY.yearly, baseDate);
  }

  static getRelativeDate(dateEntry, groupBy, baseDate) {
    // Daily
    if (groupBy < GROUP_BY.weekly) {
      const daysDifference = baseDate.diff(dateEntry, 'days');

      if (daysDifference < 1) {
        return DATE_STRINGS.today;
      }

      if (daysDifference === 1) {
        return DATE_STRINGS.yesterday;
      }
    }

    // Weekly
    if (groupBy < GROUP_BY.monthly) {
      const weeksDifference = _getWeeksDifference(baseDate, dateEntry);

      if (weeksDifference === 0) {
        return DATE_STRINGS.thisWeek;
      }

      if (weeksDifference === 1) {
        return DATE_STRINGS.lastWeek;
      }

      if (weeksDifference < 4) {
        return `${ weeksDifference } ${ DATE_STRINGS.weeksAgo }`;
      }
    }

    const yearsDifference = _getYearsDifference(baseDate, dateEntry);

    // Weekly, more than 3 weeks old
    if (groupBy < GROUP_BY.yearly) {
      const weekMonday = dateEntry.startOf('isoweek').format('MMM Do');
      if (yearsDifference < 1) {
        return weekMonday;
      }

      return `${ weekMonday }, ${ dateEntry.year() }`;
    }

    // Yearly
    if (yearsDifference < 1) {
      return DATE_STRINGS.thisYear;
    }

    if (yearsDifference === 1) {
      return DATE_STRINGS.lastYear;
    }

    return `${ yearsDifference} ${ DATE_STRINGS.yearsAgo }`;
  }
}
